import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import AuthHeader from "./AuthHeader"; // ✅ Full-page header

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const roboman_api = process.env.REACT_APP_ROBOMAN_API;

  const handleForgotPassword = async (event) => {
    event.preventDefault();
    setMessage("");
    setError("");
    setLoading(true);

    try {
      const response = await fetch(`${roboman_api}/users/password/forgot`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();
      // Backend always returns 200 with a generic message to prevent enumeration
      setMessage(data?.message || "If the email exists, a reset link has been sent.");
      setError("");
    } catch (err) {
      console.error("Error:", err);
      setError("An error occurred. Please try again.");
      setMessage("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-white overflow-x-hidden">
      {/* ✅ Light Auth Header (matches Login/Signup) */}
      <AuthHeader />

      {/* Background wash + contained glows (same family as login/signup) */}
      <div className="relative isolate flex-1 overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-indigo-50 via-white to-fuchsia-50" />
        <div className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 h-72 w-72 rounded-full bg-indigo-300/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 right-0 h-96 w-96 rounded-full bg-fuchsia-300/20 blur-3xl" />

        <div className="mx-auto grid max-w-7xl grid-cols-1 lg:grid-cols-12 gap-0 px-4 sm:px-6 lg:px-8">
          {/* Left brand panel — light card to mirror other auth pages */}
          <aside className="relative hidden lg:flex lg:col-span-6 items-center py-12">
            <div className="w-full rounded-3xl bg-white text-slate-800 ring-1 ring-slate-200 shadow-xl overflow-hidden">
              <div className="relative p-10 sm:p-12">
                {/* subtle pattern */}
                <div
                  className="absolute inset-0 opacity-60"
                  style={{
                    backgroundImage:
                      "radial-gradient(circle at 1px 1px, rgba(2,6,23,.06) 1px, transparent 0)",
                    backgroundSize: "22px 22px",
                  }}
                />
                <div className="relative">
                  <p className="text-xs tracking-[.25em] font-semibold uppercase text-fuchsia-600/70">
                    Roboman
                  </p>
                  <h1 className="mt-2 text-4xl font-extrabold leading-tight text-slate-900">
                    Reset your password
                  </h1>
                  <p className="mt-3 text-slate-600 max-w-xl">
                    Enter your registered email address and we’ll send you a secure link to create a new password. You’ll be back in your outreach cockpit in no time.
                  </p>

                  {/* capability chips for visual continuity */}
                  <div className="mt-6 flex flex-wrap gap-2">
                    {["AI Emails", "LinkedIn Steps", "Voice Notes", "Proposals", "Intent Signals"].map((c) => (
                      <span
                        key={c}
                        className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs ring-1 ring-slate-200 text-slate-700"
                      >
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> {c}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </aside>

          {/* Right form card — glassy white with gradient rail */}
          <main className="lg:col-span-6 flex items-center py-12">
            <div className="w-full mx-auto max-w-md">
              <div className="relative rounded-3xl bg-white/90 backdrop-blur-xl shadow-2xl ring-1 ring-slate-200 overflow-hidden">
                {/* thin gradient rail (brand accent) */}
                <div className="h-[3px] w-full bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600" />

                <div className="p-8 sm:p-10">
                  {/* Heading */}
                  <div className="text-center">
                    <h2 className="text-2xl font-bold text-slate-900">Forgot password</h2>
                    <p className="mt-1 text-sm text-slate-600">
                      We’ll send you a password reset link
                    </p>
                  </div>

                  {/* Form */}
                  <form className="mt-6 space-y-4" onSubmit={handleForgotPassword}>
                    <label className="block">
                      <span className="sr-only">Email address</span>
                      <input
                        type="email"
                        placeholder="Work email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                        required
                        disabled={loading}
                      />
                    </label>

                    {/* Messages */}
                    {message && <p className="text-emerald-600 text-sm">{message}</p>}
                    {error && <p className="text-red-600 text-sm">{error}</p>}

                    <button
                      type="submit"
                      disabled={loading}
                      className="relative inline-flex w-full items-center justify-center overflow-hidden rounded-xl px-5 py-3 text-sm font-semibold text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:opacity-80 disabled:cursor-not-allowed"
                    >
                      <span className="absolute inset-0 bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600" />
                      <span className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity bg-[radial-gradient(100%_100%_at_50%_0%,rgba(255,255,255,.35)_0,rgba(255,255,255,0)_60%)]" />
                      <span className="relative flex items-center gap-2">
                        {loading && (
                          <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                        )}
                        {loading ? "Sending link..." : "Send Reset Link"}
                      </span>
                    </button>
                  </form>

                  {/* Secondary actions */}
                  <button
                    className="w-full mt-4 rounded-xl px-4 py-3 text-sm font-semibold text-slate-800 bg-white ring-1 ring-slate-200 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                    onClick={() => navigate("/login")}
                  >
                    Back to Login
                  </button>

                  <p className="text-center text-sm text-slate-600 mt-6">
                    Remembered your password?{" "}
                    <Link to="/login" className="font-semibold text-indigo-600 hover:text-indigo-700">
                      Sign In
                    </Link>
                  </p>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
